import axios from 'axios';
import { auth } from '../firebase';

// Relative base URL: proxied to the Functions emulator in development (see
// package.json "proxy") and rewritten to the `api` Cloud Function by Firebase
// Hosting in production.
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach the current Firebase ID token to every request
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// API service methods
export const apiService = {
  // Authentication (sign-in itself is handled by Firebase Auth in AuthContext)
  auth: {
    verifyToken: () => api.get('/auth/verify'),
    getProfile: () => api.get('/auth/profile'),
    updateProfile: (profileData) => api.put('/auth/profile', profileData),
    logout: () => api.post('/auth/logout'),
    validateInvitation: (token) => api.get(`/auth/validate-invitation/${token}`),
    setupPassword: (token, password) => api.post('/auth/setup-password', { token, password }),
    activateInvitation: (token) => api.post('/auth/activate-invitation', { token }),
    getPasswordPolicy: (token) => api.get(`/auth/password-policy/${token || 'none'}`),
  },

  // Invitations
  invitations: {
    getPending: () => api.get('/invitations/pending'),
    send: (data) => api.post('/invitations/send', data),
    resend: (userId) => api.post(`/invitations/${userId}/resend`),
    revoke: (userId) => api.delete(`/invitations/${userId}`),
  },

  // Roles (tenant Role collection; built-in roles are system roles)
  roles: {
    getAll: () => api.get('/roles'),
    getById: (id) => api.get(`/roles/${id}`),
    create: (data) => api.post('/roles', data),
    update: (id, data) => api.put(`/roles/${id}`, data),
    delete: (id) => api.delete(`/roles/${id}`),
  },

  // Settings
  settings: {
    getSystem: () => api.get('/settings/system'),
    updateSystem: (data) => api.put('/settings/system', data),
    getCompany: () => api.get('/settings/company'),
    updateCompany: (data) => api.put('/settings/company', data),
    getEnums: () => api.get('/settings/enums'),
    updateEnums: (data) => api.put('/settings/enums', data),
    getRoles: () => api.get('/settings/roles'),
    getDepartments: () => api.get('/settings/departments'),
  },

  // Companies
  companies: {
    register: (companyData) => api.post('/companies/register', companyData),
    getProfile: () => api.get('/companies/profile'),
    updateProfile: (companyData) => api.put('/companies/profile', companyData),
    getUsers: () => api.get('/companies/users'),
    getPlans: () => api.get('/companies/plans'),
    getAllCompanies: () => api.get('/companies/admin/all'),
  },

  // Users
  users: {
    getAll: (params) => api.get('/users', { params }),
    getById: (id) => api.get(`/users/${id}`),
    invite: (userData) => api.post('/users/invite', userData),
    update: (id, userData) => api.put(`/users/${id}`, userData),
    deactivate: (id) => api.put(`/users/${id}/deactivate`),
    delete: (id) => api.delete(`/users/${id}`),
  },

  // Detections
  detections: {
    getRelated: (id) => api.get(`/detections/${id}/related`),
    getOptions: () => api.get('/detections/options'),
    getStats: () => api.get('/detections/stats/overview'),
    getAll: (params) => api.get('/detections', { params }),
    getById: (id) => api.get(`/detections/${id}`),
    create: (detectionData) => api.post('/detections', detectionData),
    processAlert: (alertData) => api.post('/detections/process-alert', alertData),
    update: (id, detectionData) => api.put(`/detections/${id}`, detectionData),
    delete: (id) => api.delete(`/detections/${id}`),
    addComment: (id, commentData) => api.post(`/detections/${id}/comments`, commentData),
    createCase: (caseData) => api.post('/detections/create-case', caseData),
    bulkUpdate: (updateData) => api.put('/detections/bulk-update', updateData),
  },

  // Cases
  cases: {
    getTimeline: (id) => api.get(`/cases/${id}/timeline`),
    generateTimeline: (id) => api.post(`/cases/${id}/timeline/generate`),
    getIncidentTimeline: (id) => api.get(`/cases/${id}/incident-timeline`),
    generateIncidentTimeline: (id) => api.post(`/cases/${id}/incident-timeline/generate`),
    getOptions: () => api.get('/cases/options'),
    getStats: () => api.get('/cases/stats/overview'),
    getAll: (params) => api.get('/cases', { params }),
    getById: (id) => api.get(`/cases/${id}`),
    create: (caseData) => api.post('/cases', caseData),
    update: (id, caseData) => api.put(`/cases/${id}`, caseData),
    delete: (id) => api.delete(`/cases/${id}`),
    addComment: (id, commentData) => api.post(`/cases/${id}/comments`, commentData),
    addDetection: (id, detectionId) => api.post(`/cases/${id}/detections`, { detectionId }),
    removeDetection: (id, detectionId) => api.delete(`/cases/${id}/detections/${detectionId}`),
  },

  // Indicators
  indicators: {
    push: (indicatorIds, { agentIds, action = 'detect' } = {}) => api.post('/indicators/push', { indicatorIds, agentIds, action }),
    pushOne: (id, { agentIds, action = 'detect' } = {}) => api.post(`/indicators/${id}/push`, { agentIds, action }),
    getOptions: () => api.get('/indicators/options'),
    getStats: () => api.get('/indicators/stats/overview'),
    addSighting: (id, data) => api.post(`/indicators/${id}/sightings`, data),
    getAll: (params) => api.get('/indicators', { params }),
    getById: (id) => api.get(`/indicators/${id}`),
    create: (indicatorData) => api.post('/indicators', indicatorData),
    update: (id, indicatorData) => api.put(`/indicators/${id}`, indicatorData),
    delete: (id) => api.delete(`/indicators/${id}`),
  },

  // Entities
  entities: {
    getTopRisk: (limit = 10) => api.get('/entities/top-risk', { params: { limit } }),
    recomputeRisk: () => api.post('/entities/recompute-risk'),
    syncEndpoints: (agentIds) => api.post('/entities/sync-endpoints', agentIds ? { agentIds } : {}),
    recomputeEntityRisk: (id) => api.post(`/entities/${id}/recompute-risk`),
    getOptions: () => api.get('/entities/options'),
    getStats: () => api.get('/entities/stats/overview'),
    addIncident: (id, data) => api.post(`/entities/${id}/incidents`, data),
    getAll: (params) => api.get('/entities', { params }),
    getById: (id) => api.get(`/entities/${id}`),
    create: (entityData) => api.post('/entities', entityData),
    update: (id, entityData) => api.put(`/entities/${id}`, entityData),
    delete: (id) => api.delete(`/entities/${id}`),
  },

  // Billing
  billing: {
    getPlans: () => api.get('/billing/plans'),
    getCurrentPlan: () => api.get('/billing/current'),
    getSubscription: () => api.get('/billing/subscription'),
    updatePlan: (planData) => api.put('/billing/plan', planData),
    cancel: () => api.post('/billing/cancel'),
    getUsage: () => api.get('/billing/usage'),
    getInvoices: () => api.get('/billing/invoices'),
  },

  // AI Services
  ai: {
    analyzeDetection: (detectionId) => api.post('/ai/analyze-detection', { detectionId }),
    correlateCases: (createCases = false) => api.post('/ai/correlate-cases', { createCases }),
    createSuggestedCase: (caseData) => api.post('/ai/create-suggested-case', caseData),
    getStatus: () => api.get('/ai/status'),
    caseSummary: (caseId) => api.post(`/ai/case-summary/${caseId}`),
    ask: (targetType, targetId, question, history = []) => api.post('/ai/ask', { targetType, targetId, question, history }),
    suggestPlaybook: (caseId) => api.post(`/ai/suggest-playbook/${caseId}`),
    runBacklog: (limit = 10) => api.post('/ai/backlog/run', { limit }),
  },

  // Threat-intel enrichment providers (VirusTotal, AbuseIPDB, OTX, GreyNoise, Shodan, abuse.ch, GeoIP, DNS)
  enrichment: {
    getProviders: () => api.get('/enrichment/providers'),
    updateProvider: (id, data) => api.put(`/enrichment/providers/${id}`, data),
    testProvider: (id) => api.post(`/enrichment/providers/${id}/test`),
    lookup: (value, { type, refresh = false, providers } = {}) => api.post('/enrichment/lookup', { value, type, refresh, providers }),
    enrichDetection: (id, refresh = false) => api.post(`/enrichment/detections/${id}`, { refresh }),
    enrichIndicator: (id, refresh = false) => api.post(`/enrichment/indicators/${id}`, { refresh }),
    getCache: () => api.get('/enrichment/cache'),
    purgeCache: () => api.delete('/enrichment/cache'),
  },

  // Agents (EDR / security product integrations) – vendor agnostic
  agents: {
    getTypes: () => api.get('/agents/types'),
    getAll: (params) => api.get('/agents', { params }),
    getById: (id) => api.get(`/agents/${id}`),
    create: (agentData) => api.post('/agents', agentData),
    update: (id, agentData) => api.put(`/agents/${id}`, agentData),
    delete: (id, force = false) => api.delete(`/agents/${id}${force ? '?force=true' : ''}`),
    testConnection: (id) => api.post(`/agents/${id}/test-connection`),
    getLogs: (id, params) => api.get(`/agents/${id}/logs`, { params }),
    getStats: (id, params) => api.get(`/agents/${id}/stats`, { params }),
    ingest: (id, options) => api.post(`/agents/${id}/ingest`, options || {}),
    ingestAll: (options) => api.post('/agents/ingest-all', options || {}),
    getAllEndpoints: (params) => api.get('/agents/endpoints', { params }),
    getEndpoints: (id, params) => api.get(`/agents/${id}/endpoints`, { params }),
    getEndpoint: (id, endpointId) => api.get(`/agents/${id}/endpoints/${endpointId}`),
    isolateEndpoint: (id, endpointId, data) => api.post(`/agents/${id}/endpoints/${endpointId}/isolate`, data || {}),
    unisolateEndpoint: (id, endpointId, data) => api.post(`/agents/${id}/endpoints/${endpointId}/unisolate`, data || {}),
    lookupIndicator: (id, type, value) => api.post(`/agents/${id}/lookup`, { type, value }),
    updateAlertStatus: (id, sourceId, status, comment) => api.post(`/agents/${id}/alerts/${encodeURIComponent(sourceId)}/status`, { status, comment }),
  },

  // Notifications
  notifications: {
    getAll: (params) => api.get('/notifications', { params }),
    markAllRead: () => api.put('/notifications/read-all'),
    markRead: (id) => api.put(`/notifications/${id}/read`),
    delete: (id) => api.delete(`/notifications/${id}`),
  },

  // Audit log
  audit: {
    getAll: (params) => api.get('/audit', { params }),
    exportUrl: () => '/api/audit/export',
  },

  // Global search
  search: (q) => api.get('/search', { params: { q } }),

  // Reports
  reports: {
    getSummary: (days = 30) => api.get('/reports/summary', { params: { days } }),
    getMitre: (days = 30) => api.get('/reports/mitre', { params: { days } }),
    getDashboard: () => api.get('/reports/dashboard'),
  },

  // Automation rules (detections)
  automation: {
    getOptions: () => api.get('/automation/options'),
    getRules: () => api.get('/automation/rules'),
    createRule: (data) => api.post('/automation/rules', data),
    updateRule: (id, data) => api.put(`/automation/rules/${id}`, data),
    deleteRule: (id) => api.delete(`/automation/rules/${id}`),
    testRule: ({ rule, ruleId, limit = 50 }) => api.post('/automation/rules/test', { rule, ruleId, limit }),
    run: ({ detectionIds, ruleIds, limit } = {}) => api.post('/automation/run', { detectionIds, ruleIds, limit }),
  },

  // External integrations (Jira)
  integrations: {
    getJira: () => api.get('/integrations/jira'),
    updateJira: (data) => api.put('/integrations/jira', data),
    testJira: (projectKey) => api.post('/integrations/jira/test', projectKey ? { projectKey } : {}),
    jiraProjects: (projectKey) => api.get('/integrations/jira/projects', { params: projectKey ? { projectKey } : {} }),
    createJiraIssue: (caseId) => api.post(`/integrations/jira/cases/${caseId}`),
    syncJiraIssue: (caseId) => api.post(`/integrations/jira/cases/${caseId}/sync`),
    unlinkJiraIssue: (caseId) => api.delete(`/integrations/jira/cases/${caseId}`),

    // ServiceNow
    getServiceNow: () => api.get('/integrations/servicenow'),
    updateServiceNow: (data) => api.put('/integrations/servicenow', data),
    testServiceNow: () => api.post('/integrations/servicenow/test'),
    createServiceNowIncident: (caseId) => api.post(`/integrations/servicenow/cases/${caseId}`),
    syncServiceNowIncident: (caseId) => api.post(`/integrations/servicenow/cases/${caseId}/sync`),
    unlinkServiceNowIncident: (caseId) => api.delete(`/integrations/servicenow/cases/${caseId}`),

    // Outbound destinations (event forwarding)
    getDestinations: () => api.get('/integrations/destinations'),
    createDestination: (data) => api.post('/integrations/destinations', data),
    updateDestination: (id, data) => api.put(`/integrations/destinations/${id}`, data),
    deleteDestination: (id) => api.delete(`/integrations/destinations/${id}`),
    testDestination: (id) => api.post(`/integrations/destinations/${id}/test`),
    getLogs: (params) => api.get('/integrations/logs', { params }),

    // Threat intelligence feeds
    getFeeds: () => api.get('/integrations/feeds'),
    createFeed: (data) => api.post('/integrations/feeds', data),
    updateFeed: (id, data) => api.put(`/integrations/feeds/${id}`, data),
    deleteFeed: (id, deactivateIndicators = false) =>
      api.delete(`/integrations/feeds/${id}${deactivateIndicators ? '?deactivateIndicators=true' : ''}`),
    testFeed: (data) => api.post('/integrations/feeds/test', data),
    syncFeed: (id) => api.post(`/integrations/feeds/${id}/sync`),
  },

  // Daily email digest
  digest: {
    preview: () => api.get('/settings/notifications/digest/preview'),
    sendTest: () => api.post('/settings/notifications/digest/send-test'),
  },

  // API keys (alert intake)
  apiKeys: {
    getAll: () => api.get('/api-keys'),
    create: (data) => api.post('/api-keys', data),
    revoke: (id) => api.delete(`/api-keys/${id}`),
  },

  // Tenant backups & restore (Settings → Backups)
  backups: {
    get: () => api.get('/backups'),
    updateSettings: (data) => api.put('/backups/settings', data),
    createExport: () => api.post('/backups/export'),
    downloadExport: () => api.post('/backups/export', {}, { params: { download: 'true' }, responseType: 'blob' }),
    exportDownload: (name) => api.get(`/backups/exports/${encodeURIComponent(name)}/download`),
    deleteExport: (name) => api.delete(`/backups/exports/${encodeURIComponent(name)}`),
    restore: (data) => api.post('/backups/restore', data),
  },

  // Case tasks & playbooks
  caseTasks: {
    add: (caseId, data) => api.post(`/cases/${caseId}/tasks`, data),
    update: (caseId, taskId, data) => api.put(`/cases/${caseId}/tasks/${taskId}`, data),
    remove: (caseId, taskId) => api.delete(`/cases/${caseId}/tasks/${taskId}`),
    applyPlaybook: (caseId, playbookId) => api.post(`/cases/${caseId}/apply-playbook`, { playbookId }),
  },
  workflows: {
    get: () => api.get('/settings/playbooks'),
    update: (data) => api.put('/settings/playbooks', data),
    testWebhook: (kind, url) => api.post('/settings/notifications/test', { kind, url }),
  },

  // Support portal: public submissions (token attached when signed in) and the organisation's tickets
  support: {
    submit: (data) => api.post('/support/tickets', data),
    getMine: () => api.get('/support/tickets/mine'),
    getById: (id) => api.get(`/support/tickets/${id}`),
    addMessage: (id, message) => api.post(`/support/tickets/${id}/messages`, { message }),
  },

  // Platform administration (Excubia staff)
  admin: {
    getOverview: () => api.get('/admin/overview'),
    getCompanies: () => api.get('/admin/companies'),
    getCompany: (id) => api.get(`/admin/companies/${id}`),
    updateCompany: (id, data) => api.put(`/admin/companies/${id}`, data),
    passwordResetLink: (companyId, uid) => api.post(`/admin/companies/${companyId}/users/${uid}/password-reset-link`),
    getPlans: () => api.get('/admin/plans'),
    getSystemSettings: () => api.get('/admin/system-settings'),
    updateSystemSettings: (data) => api.put('/admin/system-settings', data),
    getAudit: () => api.get('/admin/audit'),
    getAi: () => api.get('/admin/ai'),
    clearAiCooldown: () => api.post('/admin/ai/clear-cooldown'),
    getBackups: () => api.get('/admin/backups'),
    runBackupExport: () => api.post('/admin/backups/export'),
    exportCompany: (id) => apiService.download(`/admin/companies/${id}/export`, `excubia-tenant-${id}.json`),
    getSupportTickets: (params) => api.get('/admin/support/tickets', { params }),
    getSupportTicket: (id) => api.get(`/admin/support/tickets/${id}`),
    updateSupportTicket: (id, data) => api.put(`/admin/support/tickets/${id}`, data),
    replySupportTicket: (id, data) => api.post(`/admin/support/tickets/${id}/replies`, data),
    addSupportNote: (id, note) => api.post(`/admin/support/tickets/${id}/notes`, { note }),
  },

  /** Download a CSV export through the authenticated client. */
  download: async (path, filename) => {
    const response = await api.get(path, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Generic methods for backward compatibility
  get: (url, config) => api.get(url, config),
  post: (url, data, config) => api.post(url, data, config),
  put: (url, data, config) => api.put(url, data, config),
  delete: (url, config) => api.delete(url, config),
};

export default api;
